// src/components/squads/SquadCard.tsx
//
// Card da listagem /squads. Reusa a anatomia .sklc de `wf-skills.css`, que o
// próprio arquivo declara ser a base compartilhada dos cards de Pipelines (§09)
// e Tools (§13). Squad é o quarto ativo e entra pela mesma porta — criar um
// segundo sistema de card faria a mesma tela existir duas vezes.

import type { Squad } from '@/lib/api';
import { Users, Eye } from 'lucide-react';
import { WfKebabMenu, type WfKebabEntry } from '@/components/ui/WfKebabMenu';
import { STATUS_LABELS, TOPOLOGY_LABELS, fmtContagem, rotular } from './squad-constants';

interface SquadCardProps {
  squad: Squad;
  onClick: (squad: Squad) => void;
  /** Suprime o click do card logo após fechar o menu (ver SquadsPage). */
  onMenuOpenChange?: (open: boolean) => void;
  onViewDetails: () => void;
}

export function SquadCard({ squad, onClick, onMenuOpenChange, onViewDetails }: SquadCardProps) {
  const isGlobal = squad.scope === 'global';

  // Só "Ver detalhes" por enquanto. Promover/instanciar/excluir Squad ainda não
  // tem endpoint — colocar o item cinza aqui prometeria uma ação que não existe.
  const menuItems: WfKebabEntry[] = [
    { key: 'details', label: 'Ver detalhes', icon: Eye, onSelect: onViewDetails },
  ];

  return (
    <div
      className="sklc"
      role="button"
      tabIndex={0}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a, [role="menu"]')) return;
        onClick(squad);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(squad);
        }
      }}
      aria-label={`Squad ${squad.name || squad.slug}`}
    >
      <WfKebabMenu
        ariaLabel={`Ações da squad ${squad.slug}`}
        items={menuItems}
        onOpenChange={onMenuOpenChange}
      />

      <div className="sklc-h">
        <div className="sklc-ic">
          <Users className="ic" aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sklc-name">{squad.name || squad.slug}</div>
          <div className="sklc-slug">
            {squad.slug}
            {squad.current_version ? ` · v${squad.current_version}` : ''}
          </div>
          <div className="skl-badges">
            {squad.current_version && <span className="skl-bdg ver">v{squad.current_version}</span>}
            {/* DOD-SQD-507: GLOBAL e TENANT visualmente inequívocas. O scope vem
                explícito do backend — a UI não infere de tenant_id nulo. */}
            <span className={`skl-bdg ${isGlobal ? 'glob' : 'tnt'}`}>{squad.scope}</span>
            {squad.status && squad.status !== 'active' && (
              <span className="skl-bdg">{rotular(STATUS_LABELS, squad.status)}</span>
            )}
            {squad.topology && (
              <span className="skl-bdg">{rotular(TOPOLOGY_LABELS, squad.topology)}</span>
            )}
          </div>
        </div>
      </div>

      <p className="sklc-desc">{squad.description || 'Sem descrição.'}</p>

      <div className="sklc-foot">
        {/* fmtContagem devolve "—" quando o backend não conseguiu contar.
            Renderizar 0 aqui afirmaria "nunca executou" sem base pra isso. */}
        <span>
          Papéis <b>{fmtContagem(squad.members_count)}</b>
        </span>
        <span>
          Execuções <b>{fmtContagem(squad.runs_count)}</b>
        </span>
        {squad.orchestrator_slug && (
          <span>
            Orquestra <b>{squad.orchestrator_slug}</b>
          </span>
        )}
      </div>
    </div>
  );
}
